import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Edit,
  Plus,
  RefreshCw,
  Scale,
  Search,
  Star,
  Trash2,
  UserCircle2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { lawyerPartnersService } from '../services/lawyerPartnersService';
import {
  formatDocumentValue,
  formatOabValue,
  formatPhoneValue,
  getLawyerPartnerAppearance,
  normalizeDocumentValue,
  normalizeOabValue,
  normalizePhoneValue,
} from '../utils/lawyerPartners';

const initialForm = {
  nome: '',
  oab: '',
  email: '',
  telefone: '',
  documento: '',
  rating: 4,
  observacoes: '',
  negotiatesHonorarios: false,
  processosNegociadosCountManual: '',
  processosCompradosCountManual: '',
};

function parseManualCountInput(value) {
  if (value === '' || value === null || value === undefined) return null;
  const numberValue = Number(value);
  if (!Number.isInteger(numberValue) || numberValue < 0) return undefined;
  return numberValue;
}

function RatingStarsInput({ value = 1, onChange }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, index) => {
          const starValue = index + 1;
          const active = starValue <= Number(value || 0);

          return (
            <button
              key={starValue}
              type="button"
              onClick={() => onChange(starValue)}
              className={`rounded-lg p-1.5 transition ${active ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
              aria-label={`Definir rating ${starValue} estrela${starValue > 1 ? 's' : ''}`}
              title={`${starValue} estrela${starValue > 1 ? 's' : ''}`}
            >
              <Star className={`h-7 w-7 ${active ? 'fill-current' : ''}`} />
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-sm font-medium text-amber-900">
        {value} estrela{Number(value) > 1 ? 's' : ''}
      </p>
    </div>
  );
}

function PartnerStars({ rating }) {
  return (
    <div className="flex items-center gap-1 text-amber-500">
      {Array.from({ length: 5 }).map((_, index) => {
        const filled = index < Number(rating || 0);
        return (
          <Star
            key={index}
            className={`h-4 w-4 ${filled ? 'fill-current' : 'text-amber-200'}`}
          />
        );
      })}
    </div>
  );
}

function ProcessCountButton({ label, count, active, onClick, disabled }) {
  const isDisabled = disabled ?? !count;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
        active
          ? 'border-blue-300 bg-blue-50 text-blue-900'
          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100'
      } ${isDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold text-gray-900">{count}</div>
      </div>
      {active ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

function ProcessDetailsPanel({ title, detailsState }) {
  if (!detailsState?.open) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="text-xs text-gray-500">Clique no CNJ para abrir o processo.</p>
        </div>
      </div>

      {detailsState.loading ? (
        <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-5 text-sm text-gray-600 border border-gray-200">
          <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
          Carregando detalhes dos processos...
        </div>
      ) : detailsState.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {detailsState.error}
        </div>
      ) : detailsState.items?.length ? (
        <div className="space-y-3">
          {detailsState.items.map((item) => (
            <div key={`${detailsState.type}-${item.processId}`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_180px]">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">CNJ</div>
                  <Link
                    to={`/processo/${item.processId}`}
                    className="mt-1 inline-flex text-sm font-semibold text-blue-700 hover:text-blue-800 hover:underline"
                  >
                    {item.numeroProcesso}
                  </Link>
                  <p className="mt-1 text-xs text-gray-500">{item.tribunal || 'Tribunal não informado'}</p>
                </div>

                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Polo Ativo</div>
                  <div className="mt-1 text-sm text-gray-800">
                    {item.poloAtivo?.length ? item.poloAtivo.join(', ') : '—'}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Polo Passivo</div>
                  <div className="mt-1 text-sm text-gray-800">
                    {item.poloPassivo?.length ? item.poloPassivo.join(', ') : '—'}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Valor</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {item.valor
                      ? new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(item.valor)
                      : '—'}
                  </div>
                  {item.leadStatus && (
                    <div className="mt-2 text-xs text-gray-500">Lead: {item.leadStatus}</div>
                  )}
                </div>
              </div>

              {item.observacoes && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-gray-700">
                  <span className="font-medium text-gray-900">Observações:</span> {item.observacoes}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-5 text-sm text-gray-600">
          Nenhum processo encontrado nesta categoria.
        </div>
      )}
    </div>
  );
}

export default function AdvogadosParceirosPage() {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({ total: 0, bons: 0, neutros: 0, ruins: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [processDetailsByPartner, setProcessDetailsByPartner] = useState({});
  const formRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const nameInputRef = useRef(null);
  const shouldFocusFormRef = useRef(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [partnersData, statsData] = await Promise.all([
        lawyerPartnersService.getPartners({ search, classificacao: filter || undefined }),
        lawyerPartnersService.getStats(),
      ]);
      setPartners(partnersData || []);
      setStats(statsData || { total: 0, bons: 0, neutros: 0, ruins: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar advogados parceiros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, filter]);

  useEffect(() => {
    if (!showForm || !shouldFocusFormRef.current || !formRef.current) return;

    const stickyHeaderHeight = stickyHeaderRef.current?.getBoundingClientRect?.().height || 0;
    const formTop = formRef.current.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(formTop - stickyHeaderHeight - 32, 0);

    window.scrollTo({ top: targetTop, behavior: 'smooth' });

    window.setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select?.();
      shouldFocusFormRef.current = false;
    }, 220);
  }, [showForm, editingId]);

  const cards = useMemo(
    () => [
      { label: 'Total', value: stats.total, color: 'text-blue-600' },
      { label: 'Parceiros bons', value: stats.bons, color: 'text-green-600' },
      { label: 'Neutros', value: stats.neutros, color: 'text-orange-600' },
      { label: 'Ruins', value: stats.ruins, color: 'text-red-600' },
    ],
    [stats],
  );

  const handleEdit = (partner) => {
    shouldFocusFormRef.current = true;
    setEditingId(partner.id);
    setForm({
      nome: partner.nome || '',
      oab: normalizeOabValue(partner.oab || ''),
      email: partner.email || '',
      telefone: normalizePhoneValue(partner.telefone || ''),
      documento: normalizeDocumentValue(partner.documento || ''),
      rating: Number(partner.rating || 4),
      observacoes: partner.observacoes || '',
      negotiatesHonorarios: Boolean(partner.negotiatesHonorarios),
      processosNegociadosCountManual:
        partner.processosNegociadosCountManual == null
          ? ''
          : String(partner.processosNegociadosCountManual),
      processosCompradosCountManual:
        partner.processosCompradosCountManual == null
          ? ''
          : String(partner.processosCompradosCountManual),
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const negotiated = parseManualCountInput(form.processosNegociadosCountManual);
    const purchased = parseManualCountInput(form.processosCompradosCountManual);

    if (negotiated === undefined || purchased === undefined) {
      setError('Processos negociados e comprados devem ser inteiros maiores ou iguais a zero (ou deixe em branco para remover o valor manual).');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        nome: form.nome,
        oab: form.oab,
        email: form.email,
        telefone: form.telefone,
        documento: form.documento,
        rating: Number(form.rating),
        observacoes: form.observacoes,
        negotiatesHonorarios: form.negotiatesHonorarios,
        fonteCadastro: 'MANUAL',
        processosNegociadosCountManual: negotiated,
        processosCompradosCountManual: purchased,
      };

      if (editingId) {
        await lawyerPartnersService.updatePartner(editingId, payload);
      } else {
        await lawyerPartnersService.createPartner(payload);
      }

      resetForm();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar advogado parceiro.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Deseja remover o advogado parceiro "${nome}"?`)) return;

    try {
      await lawyerPartnersService.deletePartner(id);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover advogado parceiro.');
    }
  };

  const handleToggleProcessDetails = async (partnerId, type) => {
    const currentState = processDetailsByPartner[partnerId];
    const shouldClose = currentState?.open && currentState?.type === type;

    if (shouldClose) {
      setProcessDetailsByPartner((prev) => ({
        ...prev,
        [partnerId]: {
          ...prev[partnerId],
          open: false,
        },
      }));
      return;
    }

    const cachedItems = currentState?.cache?.[type];
    setProcessDetailsByPartner((prev) => ({
      ...prev,
      [partnerId]: {
        ...prev[partnerId],
        open: true,
        type,
        title: type === 'negociados' ? 'Processos negociados' : 'Processos comprados',
        loading: !cachedItems,
        error: '',
        items: cachedItems || [],
        cache: prev[partnerId]?.cache || {},
      },
    }));

    if (cachedItems) return;

    try {
      const response = await lawyerPartnersService.getPartnerProcessDetails(partnerId);
      const items = response?.[type] || [];
      setProcessDetailsByPartner((prev) => ({
        ...prev,
        [partnerId]: {
          ...prev[partnerId],
          loading: false,
          items,
          cache: {
            ...(prev[partnerId]?.cache || {}),
            [type]: items,
          },
        },
      }));
    } catch (err) {
      setProcessDetailsByPartner((prev) => ({
        ...prev,
        [partnerId]: {
          ...prev[partnerId],
          loading: false,
          error: err.response?.data?.error || 'Erro ao carregar detalhes dos processos.',
        },
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div ref={stickyHeaderRef} className="sticky top-4 z-20 space-y-4 rounded-2xl bg-gray-50/95 pb-1 backdrop-blur-sm supports-[backdrop-filter]:bg-gray-50/85">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Advogados Parceiros</h1>
            <p className="text-gray-600">
              Cadastre parceiros, avalie reputação comercial e destaque-os em publicações e processos.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadData}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              type="button"
              onClick={() => setShowForm((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              {showForm ? 'Fechar formulário' : 'Novo parceiro'}
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          {cards.map((card) => (
            <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-medium text-gray-500">{card.label}</div>
              <div className={`mt-2 text-3xl font-bold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[1fr,220px]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
              <input
                className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Buscar por nome, OAB, email ou telefone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 placeholder:text-gray-400"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="">Todas as classificações</option>
              <option value="BOM">Bons</option>
              <option value="NEUTRO">Neutros</option>
              <option value="RUIM">Ruins</option>
            </select>
          </div>
        </div>
      </div>

      {showForm && (
        <form ref={formRef} onSubmit={handleSubmit} className="scroll-mt-[22rem] rounded-xl border border-gray-200 bg-white p-6 pb-24 shadow-sm space-y-4 lg:scroll-mt-[24rem]">
          <div className="flex items-center gap-2 text-gray-900">
            <Scale className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">
              {editingId ? 'Editar advogado parceiro' : 'Novo advogado parceiro'}
            </h2>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_320px]">
            <div className="grid gap-4 md:grid-cols-2 xl:col-span-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Nome</span>
                <input
                  ref={nameInputRef}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  value={form.nome}
                  onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                  required
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">OAB</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  value={formatOabValue(form.oab)}
                  onChange={(e) => setForm((prev) => ({ ...prev, oab: normalizeOabValue(e.target.value) }))}
                  placeholder="Ex.: SP 123456"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Email</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Telefone</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  type="tel"
                  inputMode="numeric"
                  value={formatPhoneValue(form.telefone)}
                  onChange={(e) => setForm((prev) => ({ ...prev, telefone: normalizePhoneValue(e.target.value) }))}
                  placeholder="(11) 98765-4321"
                />
              </label>
              <label className="space-y-1 text-sm xl:col-span-2">
                <span className="font-medium text-gray-700">CPF/CNPJ</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  inputMode="numeric"
                  value={formatDocumentValue(form.documento)}
                  onChange={(e) => setForm((prev) => ({ ...prev, documento: normalizeDocumentValue(e.target.value) }))}
                  placeholder="CPF ou CNPJ"
                />
              </label>
            </div>

            <label className="space-y-1 text-sm">
              <span className="font-medium text-gray-700">Rating</span>
              <RatingStarsInput
                value={form.rating}
                onChange={(value) => setForm((prev) => ({ ...prev, rating: value }))}
              />
            </label>
          </div>

          <label className="space-y-1 text-sm block">
            <span className="font-medium text-gray-700">Observações</span>
            <textarea
              className="min-h-[90px] w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400"
              value={form.observacoes}
              onChange={(e) => setForm((prev) => ({ ...prev, observacoes: e.target.value }))}
            />
          </label>

          <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Negocia honorários</div>
              <div className="mt-1 text-xs text-gray-600">
                Marque quando o parceiro negocia a cobrança de honorários na originação.
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({ ...prev, negotiatesHonorarios: !prev.negotiatesHonorarios }))
              }
              className={`relative h-7 w-12 rounded-full border transition ${
                form.negotiatesHonorarios
                  ? 'border-green-300 bg-green-500'
                  : 'border-gray-300 bg-gray-200'
              }`}
              aria-pressed={form.negotiatesHonorarios}
              aria-label="Alternar negociação de honorários"
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition ${
                  form.negotiatesHonorarios ? 'left-5' : 'left-0.5'
                }`}
              />
            </button>
          </label>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Processos negociados e comprados</div>
              <p className="mt-1 text-xs text-gray-600">
                Valores manuais exibidos na listagem. Deixe em branco para usar apenas a contagem automática de leads/conversões.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Processos negociados</span>
                <input
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  inputMode="numeric"
                  value={form.processosNegociadosCountManual}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      processosNegociadosCountManual: e.target.value.replace(/[^\d]/g, ''),
                    }))
                  }
                  placeholder="Deixe em branco para limpar"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Processos comprados</span>
                <input
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400"
                  inputMode="numeric"
                  value={form.processosCompradosCountManual}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      processosCompradosCountManual: e.target.value.replace(/[^\d]/g, ''),
                    }))
                  }
                  placeholder="Deixe em branco para limpar"
                />
              </label>
            </div>
          </div>

          <div className="sticky bottom-4 z-10 -mx-2 rounded-2xl border border-gray-200 bg-white/95 px-4 py-4 shadow-lg backdrop-blur-sm supports-[backdrop-filter]:bg-white/90">
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-60">
                {saving ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Cadastrar parceiro'}
              </button>
            </div>
          </div>
        </form>
      )}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="space-y-4">
        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-center gap-2 p-10 text-gray-600">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              Carregando advogados parceiros...
            </div>
          </div>
        ) : partners.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-10 text-center text-gray-600">
            <UserCircle2 className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            Nenhum advogado parceiro cadastrado.
          </div>
        ) : (
          partners.map((partner) => {
            const appearance = getLawyerPartnerAppearance(partner.classificacao);
            const detailsState = processDetailsByPartner[partner.id];
            const negotiatedCount = Number(partner.processosNegociadosCount || 0);
            const purchasedCount = Number(partner.processosCompradosCount || 0);
            const hasManualCounts =
              partner.processosNegociadosCountManual !== null &&
              partner.processosNegociadosCountManual !== undefined ||
              partner.processosCompradosCountManual !== null &&
              partner.processosCompradosCountManual !== undefined;

            return (
              <div key={partner.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex-1 space-y-4 min-w-0">
                    <div className="space-y-2">
                      <PartnerStars rating={partner.rating} />
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-2xl font-semibold text-gray-900 break-words">{partner.nome}</h3>
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${appearance.badge}`}>
                          {appearance.label}
                        </span>
                      {partner.classificacao === 'RUIM' ? (
                        <span className="inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800">
                          Contato bloqueado nos leads
                        </span>
                      ) : null}
                      {partner.negotiatesHonorarios ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                          Negocia honorários
                        </span>
                      ) : null}
                      </div>
                    </div>

                    <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</div>
                        <div className="mt-1 break-all text-gray-900">{partner.email || '—'}</div>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Telefone</div>
                        <div className="mt-1 text-gray-900">{partner.telefone ? formatPhoneValue(partner.telefone) : '—'}</div>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">OAB</div>
                        <div className="mt-1 text-gray-900">{partner.oab ? formatOabValue(partner.oab) : '—'}</div>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Documento</div>
                        <div className="mt-1 text-gray-900">{partner.documento ? formatDocumentValue(partner.documento) : '—'}</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Processos negociados e comprados
                        {hasManualCounts ? (
                          <span className="ml-2 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                            Manual
                          </span>
                        ) : null}
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                      <ProcessCountButton
                        label="Processos negociados"
                        count={negotiatedCount}
                        active={detailsState?.open && detailsState?.type === 'negociados'}
                        onClick={() => handleToggleProcessDetails(partner.id, 'negociados')}
                        disabled={!negotiatedCount}
                      />
                      <ProcessCountButton
                        label="Processos comprados"
                        count={purchasedCount}
                        active={detailsState?.open && detailsState?.type === 'comprados'}
                        onClick={() => handleToggleProcessDetails(partner.id, 'comprados')}
                        disabled={!purchasedCount}
                      />
                      </div>
                    </div>

                    <ProcessDetailsPanel
                      title={detailsState?.title}
                      detailsState={detailsState}
                    />

                    {partner.observacoes && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Observações:</span> {partner.observacoes}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 self-end xl:self-start">
                    <button onClick={() => handleEdit(partner)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
                      <Edit className="h-4 w-4" />
                      Editar
                    </button>
                    <button onClick={() => handleDelete(partner.id, partner.nome)} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
